// src/components/alertesCritiques/PiecesAlertes.jsx
// Les pièces communes aux deux panneaux des alertes critiques : le panneau
// central (PanneauAlertesCritiques, une Dialog) et le panneau latéral
// (PanneauLateralAlertes, ouvert par le bouton « Alertes » de la barre).
//
// Deux dispositions :
//   - 'large'   : la grille du panneau central, recettée le 13/10/2026 et
//                 inchangée (libellé | constat | actions en colonne) ;
//   - 'etroite' : le panneau latéral de 320 px. La grille lg: casserait dans
//                 320 px (pas de container queries) : tout s'empile (libellé,
//                 précision, constat, correction, impact), puis les actions
//                 sur une rangée (« Corriger » plein, « Constater »
//                 secondaire, de même largeur) ; « Voir la liste » en pleine
//                 largeur.
//
// Ce qu'une ligne propose (le serveur compose tout, rien n'est calculé ici) :
//   - « Corriger »           : l'écran de correction ;
//   - badge « autre année »  : une correction à faire dans une session ouverte
//                              sur l'année de la ligne ;
//   - « Ouvrir la demande »  : la demande spéciale visée ;
//   - « Constater »          : la valeur restera telle quelle (validateurs,
//                              motif obligatoire). Jamais sur une information.
//                              Chaque ligne garde son motif ;
//   - badge « en attente »   : une correction attend sa validation ;
//   - « Voir la liste »      : une information qui porte son propre lien (les
//                              élèves ou le personnel sans photo) ;
//   - rien                   : simple information (une information sans lien
//                              propre n'a jamais de bouton, même quand son
//                              détecteur a un lien).
//
// Gravité « campagne » (À COMPLÉTER) : un manque à combler peu à peu, pas une
// faute. Ton gris neutre, jamais ambre ni rouge. Gravité « surveillance » (À
// SURVEILLER, classes presque pleines, 30/10/2026) : un état, ni faute ni
// manque ; même ton gris neutre.
//
// Panneau latéral (finitions de Daniel du 16/10/2026) : un groupe dont toutes
// les lignes sont traitées perd son ton de gravité (TON_TRAITE, gris neutre,
// sans « CRITIQUE ») ; le badge « Nouvelle » est clair (rouge pâle bordé),
// pour ne pas se confondre avec le « Corriger » plein placé dessous.
//
// Peu d'icônes (décision de Daniel du 14/10/2026) : aucune par ligne, aucune
// dans les badges. Le dépliage d'un détecteur garde son chevron dans le
// panneau central ; le panneau latéral n'en a pas (sa seule icône est la
// croix de fermeture, avec le Loader2 d'« Actualiser »).

import React, { useCallback, useId, useRef, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { constaterAlerte } from './apiAlertesCritiques';
import { horsSession } from './useAlertesCritiques';

// Bouton secondaire écrit en clair : la variante outline de ui/button dépend de
// variables CSS (--background, --accent, --input, --ring) que KLASYS ne définit
// pas ; elle sortait transparente, en noir pur, sans survol (constat du 13/10/2026).
export const BOUTON_SECONDAIRE = 'rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm '
  + 'hover:bg-gray-50 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-[#246990]/40';
export const BOUTON_PLEIN = 'rounded-lg bg-[#246990] text-white hover:bg-[#1f5a7a] focus-visible:ring-2 focus-visible:ring-[#246990]/40';
// Les actions d'une ligne occupent toute la colonne : elles s'alignent d'une ligne à l'autre.
const BOUTON_LIGNE = 'h-8 w-full justify-center gap-2';
// Disposition étroite : « Corriger » et « Constater » se partagent la rangée.
const BOUTON_RANGEE = 'h-8 min-w-0 flex-1 justify-center gap-2 px-3';
const BOUTON_CONSTAT = 'min-w-[120px] justify-center gap-2';
// Dans 320 px, Annuler et Confirmer se partagent la largeur, toujours égaux.
const BOUTON_CONSTAT_ETROIT = 'min-w-0 flex-1 justify-center gap-2';
// Les badges d'état d'une ligne du panneau latéral : même taille partout.
const BADGE_ETAT = 'inline-flex h-6 w-24 flex-shrink-0 items-center justify-center rounded-lg text-xs font-medium';
// « Nouvelle » : clair et bordé, jamais plein comme « Corriger ». La bordure
// est prise dans les 96 x 24 px (box-sizing de Tailwind).
const BADGE_NOUVELLE = 'border border-red-200 bg-red-50 text-red-700';
// Le nombre d'un groupe : même largeur jusqu'à trois chiffres (chiffres à chasse
// fixe), collé au bord droit de l'en-tête. Partagé avec la page de suivi.
export const BADGE_NOMBRE = 'inline-flex h-6 min-w-[2.5rem] flex-shrink-0 items-center justify-center rounded-lg px-2 '
  + 'text-[11px] font-semibold tabular-nums';

export const MOTIF_MIN = 10;
export const MOTIF_MAX = 500;
// Au-delà, un détecteur replie ses lignes : le panneau reste large plutôt que long.
export const LIGNES_VISIBLES = 20;

// Pas d'icône par détecteur : le titre du groupe et son badge de gravité suffisent.
// Le libellé s'affiche en capitales (classe uppercase) : « À COMPLÉTER ».
export const TONS = {
  critique: {
    libelle: 'Critique',
    cadre: 'border-red-200',
    entete: 'bg-red-50',
    titre: 'text-gray-900',
    badge: 'bg-red-100 text-red-700',
    texte: 'text-red-700'
  },
  importante: {
    libelle: 'Importante',
    cadre: 'border-amber-200',
    entete: 'bg-amber-50',
    titre: 'text-gray-900',
    badge: 'bg-amber-100 text-amber-800',
    texte: 'text-amber-800'
  },
  // Gris neutre, jamais ambre ni rouge : le manque de photo n'est pas une faute.
  // Badge en gray-200 : un gray-100 se perdrait sur l'en-tête gray-50.
  campagne: {
    libelle: 'À compléter',
    cadre: 'border-gray-200',
    entete: 'bg-gray-50',
    titre: 'text-gray-800',
    badge: 'bg-gray-200 text-gray-700',
    texte: 'text-gray-600'
  },
  // Le même gris neutre : une classe presque pleine n'est ni une faute ni un manque.
  surveillance: {
    libelle: 'À surveiller',
    cadre: 'border-gray-200',
    entete: 'bg-gray-50',
    titre: 'text-gray-800',
    badge: 'bg-gray-200 text-gray-700',
    texte: 'text-gray-600'
  }
};

// Panneau latéral : un groupe dont toutes les lignes sont traitées aujourd'hui.
// Gris neutre, sans libellé de gravité (ni « CRITIQUE » ni « IMPORTANTE »).
export const TON_TRAITE = {
  libelle: null,
  cadre: 'border-gray-200',
  entete: 'bg-gray-50',
  titre: 'text-gray-700',
  badge: 'bg-gray-200 text-gray-700',
  texte: 'text-gray-600'
};

// Les lignes traitées aujourd'hui (panneau latéral) : grisées, repliées, sans bouton.
const BADGES_TRAITEES = {
  reglee: { texte: 'Réglée', classe: 'bg-emerald-50 text-emerald-700' },
  constatee: { texte: 'Constatée', classe: 'bg-gray-200 text-gray-700' },
  envoyee: { texte: 'Envoyée', classe: 'bg-amber-50 text-amber-800' }
};

export const pluriel = (nombre, un, plusieurs) => (nombre > 1 ? plusieurs : un);

/**
 * Un montant ne se coupe jamais en fin de ligne (« 1 750 » / « FCFA », ni
 * « 1 » / « 750 ») : le serveur sépare les milliers et l'unité par une espace
 * ordinaire ; à l'affichage seulement, elle devient insécable (fine entre les
 * milliers, normale avant « FCFA »). Les textes du serveur ne changent pas.
 */
export const montantsInsecables = (texte) => (
  typeof texte === 'string'
    ? texte.replace(/(\d) (?=\d{3}(?!\d))/g, '$1 ').replace(/(\d) FCFA/g, '$1 FCFA')
    : texte
);

// --- Le constat d'une ligne ------------------------------------------------------------

/**
 * L'état des constats d'un panneau : la ligne dépliée, un motif par ligne
 * (passer d'une ligne à l'autre ne perd rien de ce qui a été tapé), l'envoi
 * en cours, et la confirmation.
 *
 * @param {{ onLigneConstatee: (detecteurId: string, cle: string) => void,
 *   onActualiser?: () => Promise<boolean>, notifier: Function,
 *   signalerEchec: Function, prochaineFois?: string }} options
 *   `prochaineFois` : quand le panneau sera à jour s'il n'a pas pu être relu
 *   après un constat refusé (« au prochain rappel », « à la prochaine lecture »).
 */
export function useConstats({ onLigneConstatee, onActualiser, notifier, signalerEchec, prochaineFois = 'au prochain rappel' }) {
  const [enConstat, setEnConstat] = useState(null); // `${detecteur}|${cle}` de la ligne dépliée
  const [motifs, setMotifs] = useState({});
  const [envoi, setEnvoi] = useState(false);
  // Verrou immédiat : `envoi` ne désactive les boutons qu'au rendu suivant.
  const envoiEnCours = useRef(false);

  const oublierMotif = useCallback((cleConstat) => {
    setMotifs((precedents) => {
      if (!(cleConstat in precedents)) return precedents;
      const suivants = { ...precedents };
      delete suivants[cleConstat];
      return suivants;
    });
  }, []);

  /** Repart d'un panneau propre : aucun motif déplié, aucun motif gardé. */
  const reinitialiser = useCallback(() => {
    setEnConstat(null);
    setMotifs({});
  }, []);

  const changerMotif = useCallback((cleConstat, texte) => {
    setMotifs((precedents) => ({ ...precedents, [cleConstat]: texte }));
  }, []);

  const ouvrirConstat = (cleConstat) => {
    if (envoiEnCours.current) return;
    setEnConstat(cleConstat);
  };

  // Abandonner le constat de la ligne dépliée : son motif s'efface.
  const annulerConstat = () => {
    if (envoiEnCours.current) return;
    if (enConstat) oublierMotif(enConstat);
    setEnConstat(null);
  };

  /** La ligne d'un motif a quitté la liste (relecture) : son motif s'efface et se replie. */
  const abandonnerConstat = useCallback((cleConstat) => {
    if (envoiEnCours.current) return;
    oublierMotif(cleConstat);
    setEnConstat((courant) => (courant === cleConstat ? null : courant));
  }, [oublierMotif]);

  const confirmerConstat = async (detecteurId, ligne) => {
    const cleConstat = `${detecteurId}|${ligne.cle}`;
    const texte = (motifs[cleConstat] || '').trim();
    if (texte.length < MOTIF_MIN || envoiEnCours.current) return;
    envoiEnCours.current = true;
    setEnvoi(true);
    try {
      await constaterAlerte({
        detecteur: detecteurId,
        entite_type: ligne.entite_type,
        entite_id: ligne.entite_id,
        empreinte: ligne.empreinte,
        motif: texte
      });
      setEnConstat(null);
      oublierMotif(cleConstat);
      notifier('Constat enregistré. La ligne ne sera plus signalée tant que la valeur ne change pas.', 'success');
      onLigneConstatee(detecteurId, ligne.cle);
    } catch (erreur) {
      if (erreur?.statut === 409) {
        // Le constat n'a plus d'objet tel quel : le motif se replie.
        setEnConstat(null);
        oublierMotif(cleConstat);
        if (erreur.code === 'DEJA_CONSTATEE') {
          // Un autre validateur, ou un autre onglet, l'a déjà fait : la ligne s'en va.
          notifier('Cette anomalie avait déjà été constatée.', 'info');
          onLigneConstatee(detecteurId, ligne.cle);
        } else {
          // La valeur a bougé depuis l'affichage : le panneau se relit de lui-même.
          const actualise = onActualiser ? await onActualiser() : false;
          signalerEchec(
            actualise
              ? 'La situation a changé depuis l\'affichage du panneau : il vient d\'être actualisé.'
              : 'La situation a changé depuis l\'affichage du panneau, et il n\'a pas pu être relu. '
                + `Il sera à jour ${prochaineFois}.`,
            'Constat impossible'
          );
        }
      } else {
        signalerEchec(erreur?.message || 'Le constat n\'a pas pu être enregistré.', 'Constat impossible');
      }
    } finally {
      envoiEnCours.current = false;
      setEnvoi(false);
    }
  };

  return {
    enConstat,
    motifs,
    envoi,
    envoiEnCours,
    reinitialiser,
    changerMotif,
    ouvrirConstat,
    annulerConstat,
    abandonnerConstat,
    confirmerConstat
  };
}

export function FormulaireConstat({ motif, onMotif, envoi, onConfirmer, onAnnuler, disposition = 'large' }) {
  const id = useId();
  const aide = useId();
  const longueur = motif.trim().length;
  const valide = longueur >= MOTIF_MIN;
  const etroite = disposition === 'etroite';
  const bouton = etroite ? BOUTON_CONSTAT_ETROIT : BOUTON_CONSTAT;

  const soumettre = (evenement) => {
    evenement.preventDefault();
    if (valide && !envoi) onConfirmer();
  };

  // Panneau latéral : Échap replie le motif (abandon, comme « Annuler »), et
  // seulement lui. Dans le panneau central, c'est la Dialog qui s'en charge.
  const surTouche = etroite
    ? (evenement) => {
      if (evenement.key !== 'Escape' || envoi) return;
      evenement.preventDefault();
      onAnnuler();
    }
    : undefined;

  return (
    <form onSubmit={soumettre} onKeyDown={surTouche} className="mt-3 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3">
      <label htmlFor={id} className="mb-1 block text-sm text-gray-700">Motif du constat (obligatoire)</label>
      <textarea
        id={id}
        rows={etroite ? 3 : 2}
        maxLength={MOTIF_MAX}
        autoFocus
        required
        aria-required="true"
        aria-describedby={aide}
        value={motif}
        onChange={(e) => onMotif(e.target.value)}
        placeholder="Pourquoi cette valeur reste telle quelle"
        className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
      />
      <div className={etroite ? 'mt-2 space-y-2' : 'mt-2 flex flex-wrap items-center justify-between gap-2'}>
        <p id={aide} className="text-[11px] text-gray-500">
          {valide
            ? "La ligne ne sera plus signalée tant que la valeur ne change pas."
            : `Encore ${MOTIF_MIN - longueur} ${pluriel(MOTIF_MIN - longueur, 'caractère', 'caractères')} au moins.`}
        </p>
        <div className="flex gap-2">
          <Button type="button" size="sm" className={`${bouton} ${BOUTON_SECONDAIRE}`} onClick={onAnnuler} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" size="sm" className={`${bouton} ${BOUTON_PLEIN}`} disabled={!valide || envoi}>
            {envoi && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirmer
          </Button>
        </div>
      </div>
    </form>
  );
}

// --- Une ligne -----------------------------------------------------------------------------

/**
 * Une ligne à traiter, ou une information.
 * @param {'large'|'etroite'} disposition
 * @param {boolean} enCours - panneau latéral : la ligne est partie en correction
 * @param {boolean} nouvelle - panneau latéral : apparue après la première lecture du jour,
 *   vue depuis l'ouverture (composerPanneau)
 * @param {(lien: string) => void} onCorriger - « Corriger » et « Ouvrir la demande »
 * @param {(lien: string) => void} onVoirListe
 */
export function LigneAlerte({
  ligne, lienDetecteur, anneeSession, enConstat, motif, envoi,
  onMotif, onCorriger, onVoirListe, onConstater, onConfirmer, onAnnuler,
  disposition = 'large', enCours = false, nouvelle = false
}) {
  const lien = ligne.lien || lienDetecteur || null;
  // Une information ne se constate pas : elle se règle ailleurs (site de référence…).
  const peutConstater = ligne.action !== 'information'
    && (ligne.constatable === true || ligne.action === 'constater');
  // Seul le lien propre de la ligne, jamais le repli sur celui du détecteur :
  // les informations d'avant (site distant, frais encaissé) restent sans bouton.
  const lienListe = ligne.action === 'information' && ligne.lien ? ligne.lien : null;
  const autreAnnee = ligne.annee_scolaire_id != null && anneeSession != null
    && String(ligne.annee_scolaire_id) !== String(anneeSession);
  // L'écran de correction refuserait un élève d'une autre année que la session.
  const aCorrigerAilleurs = horsSession(ligne, anneeSession);
  const corrigible = ligne.action === 'corriger' && !aCorrigerAilleurs && lien;
  const validable = ligne.action === 'valider' && lien;

  const badgeAnnee = autreAnnee && (
    <span className="mt-1 inline-flex h-6 items-center rounded-lg bg-gray-100 px-2 text-[11px] font-semibold text-gray-600">
      Année {ligne.annee_nom || ligne.annee_scolaire_id}
    </span>
  );
  const ailleurs = aCorrigerAilleurs && (
    <span className="inline-flex min-h-8 w-full items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 text-center text-xs font-medium text-gray-600">
      À corriger dans une session ouverte sur l'année {ligne.annee_nom || ligne.annee_scolaire_id}
    </span>
  );
  const enAttente = ligne.action === 'attendre_validation' && (
    <span className="inline-flex min-h-8 w-full items-center justify-center rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-center text-xs font-medium text-amber-800">
      Correction envoyée - en attente de validation
    </span>
  );
  const formulaire = enConstat && (
    <FormulaireConstat
      motif={motif}
      onMotif={onMotif}
      envoi={envoi}
      onConfirmer={onConfirmer}
      onAnnuler={onAnnuler}
      disposition={disposition}
    />
  );

  if (disposition === 'etroite') {
    const bouton = corrigible || validable ? (
      <Button type="button" size="sm" className={`${BOUTON_RANGEE} ${BOUTON_PLEIN}`} onClick={() => onCorriger(lien)} disabled={envoi}>
        {validable ? 'Ouvrir la demande' : 'Corriger'}
      </Button>
    ) : null;
    const constater = peutConstater && !enConstat ? (
      <Button type="button" size="sm" className={`${BOUTON_RANGEE} ${BOUTON_SECONDAIRE}`} onClick={onConstater} disabled={envoi}>
        Constater
      </Button>
    ) : null;
    const actions = ailleurs || enAttente || bouton || constater || lienListe;

    return (
      // Bordure du haut par ligne (pas de divide-y) : la barre « En cours »
      // garde sa couleur, le filet gris des autres côtés n'y touche pas. Ses
      // 3 px sont pris sur la marge de gauche (9 + 3 = 12 px, comme px-3) :
      // le texte reste aligné sur celui des autres lignes.
      <li className={`border-t border-t-gray-100 py-3 first:border-t-0${enCours ? ' border-l-[3px] border-l-[#246990] pl-[9px] pr-3' : ' px-3'}`}>
        {/* Seul le libellé partage sa rangée avec le badge (96 px) : la
            précision passe dessous, en pleine largeur, sans se replier. */}
        <div className="flex items-start justify-between gap-2">
          <p className="min-w-0 flex-1 break-words text-sm font-semibold text-gray-900">{ligne.libelle}</p>
          {enCours && <span className={`${BADGE_ETAT} bg-[#eef7fb] text-[#246990]`}>En cours</span>}
          {!enCours && nouvelle && <span className={`${BADGE_ETAT} ${BADGE_NOUVELLE}`}>Nouvelle</span>}
        </div>
        {ligne.precision && <p className="break-words text-xs text-gray-500">{montantsInsecables(ligne.precision)}</p>}
        {badgeAnnee}

        {(ligne.constat || ligne.correction || ligne.impact) && (
          <div className="mt-1 space-y-0.5">
            {ligne.constat && <p className="break-words text-sm text-gray-700">{montantsInsecables(ligne.constat)}</p>}
            {ligne.correction && <p className="break-words text-sm font-semibold text-gray-900">{montantsInsecables(ligne.correction)}</p>}
            {ligne.impact && <p className="break-words text-xs text-gray-500">{montantsInsecables(ligne.impact)}</p>}
          </div>
        )}

        {actions && (
          <div className="mt-2 space-y-2">
            {ailleurs}
            {enAttente}
            {(bouton || constater) && (
              <div className="flex gap-2">
                {bouton}
                {constater}
              </div>
            )}
            {lienListe && (
              <Button
                type="button"
                size="sm"
                className={`${BOUTON_LIGNE} ${BOUTON_SECONDAIRE}`}
                onClick={() => onVoirListe(lienListe)}
                disabled={envoi}
              >
                Voir la liste
              </Button>
            )}
          </div>
        )}

        {formulaire}
      </li>
    );
  }

  return (
    <li className="px-4 py-3">
      <div className="grid gap-3 lg:grid-cols-[minmax(0,15rem)_minmax(0,1fr)_12.5rem] lg:items-start">
        <div className="min-w-0">
          <p className="break-words text-sm font-semibold text-gray-900">{ligne.libelle}</p>
          {ligne.precision && <p className="break-words text-xs text-gray-500">{montantsInsecables(ligne.precision)}</p>}
          {badgeAnnee}
        </div>

        <div className="min-w-0 space-y-0.5">
          <p className="break-words text-sm text-gray-700">{montantsInsecables(ligne.constat)}</p>
          {ligne.correction && <p className="break-words text-sm font-semibold text-gray-900">{montantsInsecables(ligne.correction)}</p>}
          {ligne.impact && <p className="break-words text-xs text-gray-500">{montantsInsecables(ligne.impact)}</p>}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
          {ailleurs}
          {corrigible && (
            <Button
              type="button"
              size="sm"
              className={`${BOUTON_LIGNE} ${BOUTON_PLEIN}`}
              onClick={() => onCorriger(lien)}
              disabled={envoi}
            >
              Corriger
            </Button>
          )}
          {validable && (
            <Button
              type="button"
              size="sm"
              className={`${BOUTON_LIGNE} ${BOUTON_PLEIN}`}
              onClick={() => onCorriger(lien)}
              disabled={envoi}
            >
              Ouvrir la demande
            </Button>
          )}
          {enAttente}
          {lienListe && (
            <Button
              type="button"
              size="sm"
              className={`${BOUTON_LIGNE} ${BOUTON_SECONDAIRE}`}
              onClick={() => onVoirListe(lienListe)}
              disabled={envoi}
            >
              Voir la liste
            </Button>
          )}
          {peutConstater && !enConstat && (
            <Button type="button" size="sm" className={`${BOUTON_LIGNE} ${BOUTON_SECONDAIRE}`} onClick={onConstater} disabled={envoi}>
              Constater
            </Button>
          )}
        </div>
      </div>

      {formulaire}
    </li>
  );
}

/**
 * Panneau latéral : une ligne traitée aujourd'hui, grisée et repliée (le
 * libellé et son état), sans bouton. `statut` : 'reglee', 'constatee' ou
 * 'envoyee'.
 */
export function LigneTraitee({ ligne, statut }) {
  const badge = BADGES_TRAITEES[statut] || BADGES_TRAITEES.reglee;
  return (
    <li className="flex items-start justify-between gap-2 border-t border-t-gray-100 bg-gray-50 px-3 py-2 first:border-t-0">
      <div className="min-w-0 flex-1">
        <p className="break-words text-sm text-gray-500">{ligne?.libelle || 'Ligne traitée'}</p>
        {statut === 'envoyee' && <p className="text-[11px] text-gray-400">en attente de validation</p>}
      </div>
      <span className={`${BADGE_ETAT} ${badge.classe}`}>{badge.texte}</span>
    </li>
  );
}

// --- Un détecteur ----------------------------------------------------------------------------

/**
 * Un groupe de lignes, au ton de la gravité du détecteur.
 * @param {object} detecteur - { titre, description, gravite, total, lignes }
 * @param {Array} [lignes] - les lignes à afficher (par défaut detecteur.lignes)
 * @param {number|null} [compte] - le nombre du badge (par défaut detecteur.total) ;
 *   disposition étroite : pas de badge à 0
 * @param {boolean} [traite] - panneau latéral : toutes les lignes sont traitées,
 *   le groupe prend le ton neutre (TON_TRAITE), sans libellé de gravité
 * @param {(ligne) => React.ReactNode} children - le rendu d'une ligne
 */
export function GroupeDetecteur({
  detecteur, lignes = null, compte = null, deplie, onDeplier, disposition = 'large', traite = false, children
}) {
  const ton = traite ? TON_TRAITE : (TONS[detecteur.gravite] || TONS.importante);
  const toutes = lignes || detecteur.lignes || [];
  const etroite = disposition === 'etroite';
  let nombre = compte;
  if (nombre === null) nombre = Number.isFinite(Number(detecteur.total)) ? Number(detecteur.total) : toutes.length;
  const visibles = deplie ? toutes : toutes.slice(0, LIGNES_VISIBLES);
  const reste = toutes.length - visibles.length;

  return (
    <section className={`overflow-hidden rounded-lg border ${ton.cadre}`}>
      <header className={`${etroite ? 'px-3 py-2' : 'px-4 py-3'} ${ton.entete}`}>
        {/* Le nombre a sa colonne fixe, au bord droit de l'en-tête : d'un groupe
            à l'autre, il tombe au même endroit, quelle que soit la longueur du
            titre (il le suivait et se décalait, constat de Daniel du 31/10/2026). */}
        <div className="flex items-start gap-2">
          <div className="flex min-h-6 min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
            <h3 className={`text-sm font-semibold ${ton.titre}`}>{detecteur.titre}</h3>
            {ton.libelle && (
              <span className={`text-[11px] font-semibold uppercase tracking-wide ${ton.texte}`}>{ton.libelle}</span>
            )}
          </div>
          {(!etroite || nombre > 0) && <span className={`${BADGE_NOMBRE} ${ton.badge}`}>{nombre}</span>}
        </div>
        {detecteur.description && <p className="mt-0.5 text-xs text-gray-600">{detecteur.description}</p>}
      </header>

      <ul className={etroite ? 'bg-white' : 'divide-y divide-gray-100 bg-white'}>
        {visibles.map(children)}
      </ul>

      {reste > 0 && (
        <div className={`border-t border-gray-100 bg-white ${etroite ? 'px-3' : 'px-4'} py-2`}>
          <Button
            type="button"
            size="sm"
            className="gap-2 rounded-lg text-[#246990] hover:bg-[#eef7fb] hover:text-[#1f5a7a] focus-visible:ring-2 focus-visible:ring-[#246990]/40"
            onClick={onDeplier}
          >
            {!etroite && <ChevronDown className="h-4 w-4" />}
            Afficher {pluriel(reste, 'l\'autre ligne', `les ${reste} autres lignes`)}
          </Button>
        </div>
      )}
    </section>
  );
}

/** Les vérifications en échec : elles seront refaites (`suite` : « au prochain rappel »…). */
export function VerificationsEnEchec({ erreurs, suite }) {
  if (!Array.isArray(erreurs) || erreurs.length === 0) return null;
  return (
    <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
      {erreurs.length > 1
        ? `${erreurs.length} vérifications n'ont pas abouti ; elles seront refaites ${suite}.`
        : `Une vérification n'a pas abouti ; elle sera refaite ${suite}.`}
    </p>
  );
}
